/**
 * Personalization tags an admin can drop into a message body. They show up in
 * the editor as a human-readable token (e.g. `[Learner name]`) that the admin
 * can type around or delete like normal text, and are converted to the
 * platform's `<ptag>…</ptag>` placeholder when the email body is compiled for
 * sending. The backend substitutes the real value (per learner / enrollment)
 * at send time.
 *
 * Only Learner name and Enrollment name are supported for now.
 */
export type PersonalizationTagId = 'LEARNER_NAME' | 'ENROLLMENT_NAME';

export type PersonalizationTag = {
  id: PersonalizationTagId;
  /** Human-readable token inserted into / shown in the editor. */
  display: string;
  /** Placeholder emitted in the compiled HTML; the backend substitutes it. */
  placeholder: string;
};

export const PERSONALIZATION_TAGS: readonly PersonalizationTag[] = [
  { id: 'LEARNER_NAME', display: '[Learner name]', placeholder: '<ptag>LEARNER_NAME</ptag>' },
  { id: 'ENROLLMENT_NAME', display: '[Enrollment]', placeholder: '<ptag>ENROLLMENT_NAME</ptag>' },
];

/**
 * Swaps each tag's human-readable display token for its `<ptag>` placeholder.
 * Run on the compiled HTML when preparing the body for send.
 */
export const applyPersonalizationPlaceholders = (html: string): string =>
  PERSONALIZATION_TAGS.reduce((acc, tag) => acc.split(tag.display).join(tag.placeholder), html);
